import { ComponentClassNames } from '../../util/classNames';
import { PaletteColor } from '../../util/colors';
import { getGutterValue } from '../../util/styles/gutters';
import { IStyle, ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';

import { LayoutListProps } from '.';

export interface LayoutListStyleProps {
  readonly theme: ITheme;
  readonly direction: LayoutListProps['direction'];
  readonly className?: LayoutListProps['className'];
  readonly showSeparator: LayoutListProps['showSeparator'];
  readonly separatorColor: PaletteColor;
  readonly itemSpacing?: number;
  readonly align: LayoutListProps['align'];
  readonly preventItemWrap?: LayoutListProps['preventItemWrap'];
  readonly fitToContent?: boolean;
}

type LayoutListClassNames = ComponentClassNames<'layoutList'>;
type GetClassNames = (props: LayoutListStyleProps) => LayoutListClassNames;

export const getClassNames: GetClassNames = (props) =>
  getClassNamesMemoized(
    props.align,
    props.className,
    props.direction,
    props.fitToContent,
    props.itemSpacing,
    props.preventItemWrap,
    props.separatorColor,
    props.showSeparator,
    props.theme
  );

const getClassNamesMemoized = memoizeFunction(
  (
    align: LayoutListStyleProps['align'],
    className: LayoutListStyleProps['className'],
    direction: LayoutListStyleProps['direction'],
    fitToContent: LayoutListStyleProps['fitToContent'],
    itemSpacing: LayoutListStyleProps['itemSpacing'],
    preventItemWrap: LayoutListStyleProps['preventItemWrap'],
    separatorColor: LayoutListStyleProps['separatorColor'],
    showSeparator: LayoutListStyleProps['showSeparator'],
    theme: LayoutListStyleProps['theme']
  ) => {
    const isHorizontal = direction === 'horizontal';

    const directedAlign = isHorizontal ? align : undefined;
    const defaultItemSpacing = isHorizontal ? 1 : 0;
    const actualItemSpacing = itemSpacing != null ? itemSpacing : defaultItemSpacing;
    const itemSpacingValue = getGutterValue(actualItemSpacing);
    const borderValue = showSeparator ? `solid 1px ${theme.palette[separatorColor]}` : undefined;

    const horizontalChild: IStyle = {
      padding: 0,
      margin: 0,
      paddingRight: itemSpacingValue,
      paddingLeft: showSeparator ? itemSpacingValue : 0,
      borderLeft: borderValue,
      selectors: { ':first-child': { paddingLeft: 0, borderLeft: 0 }, ':last-child': { paddingRight: 0 } },
    };

    const verticalChild: IStyle = {
      padding: 0,
      margin: 0,
      display: 'block',
      paddingBottom: itemSpacingValue,
      paddingTop: showSeparator ? itemSpacingValue : 0,
      borderTop: borderValue,
      selectors: { ':first-child': { paddingTop: 0, borderTop: 0 }, ':last-child': { paddingBottom: 0 } },
    };

    const wrapValue = preventItemWrap ? 'nowrap' : 'wrap';
    const flexDisplay = fitToContent ? 'inline-flex' : 'flex';
    const display = isHorizontal ? flexDisplay : 'block';
    const flexFlow = isHorizontal ? `row ${wrapValue}` : undefined;
    const justifyContent = directedAlign === 'right' ? 'flex-end' : undefined;

    return mergeStyleSets({
      layoutList: [
        {
          padding: 0,
          margin: 0,
          listStyle: 'none',
          display,
          flexFlow,
          justifyContent,
          selectors: {
            '> .y-layoutList--item': isHorizontal ? horizontalChild : verticalChild,
          },
        },
        className,
      ],
    });
  }
);
